/**
 * 이상한 방 - 소품 이름 하나 (`item.<카탈로그 id>` 접두사의 유일한 원본)
 *
 * 근거 문서: `src/i18n/GLOSSARY.md` 4.2절(조립하는 키) · `CLAUDE.md`("하드코딩한 한국어를 화면에 남기지 마라")
 *            로드맵 7절 P2의 남은 것("소품 이름이 영어에 없다 - 결과·리빌 문장이 `A new ㄱ자 책상 showed up`이 된다")
 *
 * ## 왜 생겼나
 *
 * `catalog/`의 `CatalogEntry.name`이 한국어 리터럴이고, `game/reveal.ts`가 그 값을 **사전을 거치지 않고**
 * 문장에 끼워 넣고 있었다. 그래서 영어로 놀면 `A new ㄱ자 책상 showed up in the Room`이 나왔다 - 소품
 * 151종이 전부 그랬다. 이 저장소가 "문구는 반드시 i18n을 거친다"를 규약으로 못박아 둔 자리에 정확히
 * 151개의 예외가 있던 셈이다.
 *
 * ## 사전이 유일한 출처다 - `catalog`의 `name`으로 떨어지지 않는다
 *
 * `zoneName.ts`와 같은 결정이다. 카탈로그의 `name`은 **에디터·디버그용 키 문자열**이지 화면 문구가 아니다
 * (`floorPlanModel.Room.name`과 같은 지위). 사전에 없으면 이름을 **생략한다**(`undefined`) - 폴백으로
 * 한국어 리터럴을 떨어뜨리면 영어 화면에 한국어가 다시 새고, 그건 지금 고치는 그 고장이다. 부르는 쪽이
 * 이름 없는 문장을 그린다(`reveal.unknownItem` = "무언가" / "something").
 *
 * 키가 빠지는 것은 데이터 오류이고 `i18n/index.test.ts`가 카탈로그 전체에 ko/en 키가 있는지 본다.
 *
 * ## 영어는 소문자·단수·관사 없이 적는다
 *
 * 문장이 `'A new {item} showed up in the {room}'` · `'The {item} vanished'` 꼴이라 관사와 시제가 이미
 * 템플릿에 있다. 이름에 `A`나 `The`를 넣으면 `A new a corner desk`가 된다. 관사가 늘 `new` 앞에 오므로
 * a/an 문제도 생기지 않는다(`A new apple`).
 *
 * ## 접두사를 상수로 빼지 않는다
 *
 * `i18n/index.test.ts`의 미참조 키 검사가 `` `item.${'${'}…}` `` 꼴의 **템플릿 접두사**를 읽어 "이 접두사의
 * 키들은 코드가 부른다"고 판단한다. 상수 뒤에 숨기면 사전의 소품 이름 151개가 전부 죽은 키로 잡힌다 -
 * 검사가 읽을 수 있는 모양으로 적는 것이 이 한 줄의 계약이다(`zoneName.ts`와 같은 이유).
 *
 * `i18n/`은 최하층이라 `core`도 `catalog`도 보지 않는다 - 받는 것은 문자열 id 하나다.
 */

import { hasKey, t } from './index'

/**
 * 카탈로그 id → 지금 언어의 소품 이름. 사전에 없거나 id가 없으면 `undefined`다 - 부르는 쪽이
 * 이름 없는 문장을 그린다(빈 문자열을 돌려주면 "이름이 있는데 비었다"와 구별되지 않는다).
 */
export function itemName(itemId: string | null | undefined): string | undefined {
  if (itemId === null || itemId === undefined) return undefined
  const key = `item.${itemId}`
  return hasKey(key) ? t(key) : undefined
}
